import pedidoVentaService, { PedidoVenta, PedidoVentaDetalle } from '../services/pedidoVentaService';
import type { VentaPendienteMaster } from '../services/ventasSeguimientoService';
import { normalizeItemNotas } from './formatters';
import {
  estadoDesdePedido,
  normalizeEstadoPedidoMaster,
} from './pedidoVentaEstados';

/** Resuelve el estado: pedido origen (fuente de verdad) y luego el endpoint de pendientes. */
export function resolverEstadoPendientePedido(
  pendiente: VentaPendienteMaster,
  pedido?: Parameters<typeof estadoDesdePedido>[0] | null,
): string {
  const desdePedido = pedido ? estadoDesdePedido(pedido) : undefined;
  return normalizeEstadoPedidoMaster(desdePedido ?? pendiente.estadoMaster);
}

function findDetallePedidoParaPendiente(
  pedido: PedidoVenta,
  pendiente: VentaPendienteMaster,
): PedidoVentaDetalle | undefined {
  const detalles = pedido.detalles ?? [];
  const itemMaster = pendiente.nroItemMaster;
  const codigo = (pendiente.codigoArticulo ?? '').trim();

  const porItem = detalles.find((d) => Number(d.nroItem) === Number(itemMaster));
  if (porItem) return porItem;

  if (codigo) {
    const porCodigo = detalles.filter((d) => String(d.codigoArticulo ?? '').trim() === codigo);
    if (porCodigo.length === 1) return porCodigo[0];
    const porItemEnCodigo = porCodigo.find((d) => Number(d.nroItem) === Number(itemMaster));
    if (porItemEnCodigo) return porItemEnCodigo;
  }

  return undefined;
}

function notasDetallePedido(d: PedidoVentaDetalle): string {
  const raw = d as PedidoVentaDetalle & { Notas?: string | null };
  return normalizeItemNotas(raw.notas ?? raw.Notas);
}

/** Completa estadoMaster, descripción de cabecera y notas de renglón desde el pedido origen. */
export async function enrichPendientesConEstadoPedido(
  rows: VentaPendienteMaster[],
): Promise<VentaPendienteMaster[]> {
  if (rows.length === 0) return rows;

  const formularios = Array.from(
    new Set(rows.map((p) => p.nroFormularioMaster).filter((n) => n > 0)),
  );
  const pedidosPorFormulario = new Map<number, Awaited<ReturnType<typeof pedidoVentaService.getPedidoVenta>>>();

  await Promise.all(
    formularios.map(async (nroFormulario) => {
      try {
        const pedido = await pedidoVentaService.getPedidoVenta(nroFormulario, { sinCache: true });
        pedidosPorFormulario.set(nroFormulario, pedido);
      } catch {
        /* pedido no disponible */
      }
    }),
  );

  return rows.map((p) => {
    const pedido = pedidosPorFormulario.get(p.nroFormularioMaster);
    const descripcionPedido = String(pedido?.descripcion ?? '').trim() || undefined;
    const detalle = pedido ? findDetallePedidoParaPendiente(pedido, p) : undefined;
    const notasDetalle = detalle ? notasDetallePedido(detalle).trim() : undefined;
    const notas =
      notasDetalle && descripcionPedido && notasDetalle === descripcionPedido
        ? undefined
        : notasDetalle || undefined;

    return {
      ...p,
      estadoMaster: resolverEstadoPendientePedido(p, pedido),
      descripcionPedido: descripcionPedido ?? p.descripcionPedido,
      ...(pedido ? { notas } : {}),
    };
  });
}
